"use client";

import React, { useEffect, useRef, useState } from "react";
import { Search, Cpu, Zap, TrendingUp } from "lucide-react";
const steps = [{
  id: "01",
  title: "Sourcing",
  description: "Proprietary pipeline of brownfield and greenfield sites in power-secured corridors with NNN leases, substation adjacency, and 10+ acre expansion capacity.",
  icon: Search
}, {
  id: "02",
  title: "Architecture",
  description: "Integrated technical/financial diligence on power, permits, cooling, and structure, targeting 40/60 equity-to-debt ratios with institutional co-investment participation.",
  icon: Cpu
}, {
  id: "03",
  title: "Deployment",
  description: "Phased 20–40MW modular builds with GPU-aligned supply, liquid cooling integration, and hyperscaler-ready densities, de-risked through hardware partnerships.",
  icon: Zap
}, {
  id: "04",
  title: "Monetization",
  description: "Monetization via colocation and managed services yield, AIaaS/GPUaaS owner-operator conversion, pad-ready monetization, and full platform exit at institutional multiples.",
  icon: TrendingUp
}];
export default function TheDeploymentPipeline() {
  const [progress, setProgress] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);
  useEffect(() => {
    const handleScroll = () => {
      if (!sectionRef.current) return;
      const rect = sectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const scrollableDistance = rect.height - windowHeight;
      const scrolled = -rect.top;
      if (scrollableDistance > 0) {
        const p = Math.max(0, Math.min(100, scrolled / scrollableDistance * 100));
        setProgress(p);
      } else {
        setProgress(rect.top > 0 ? 0 : 100);
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="the-deployment-pipeline" ref={sectionRef} className="relative bg-[#FDFAF3] text-[#1C1917] py-24 md:py-32">
      <div className="mx-auto w-full max-w-7xl px-6 md:px-10">
        <div className="mb-20 text-center">
          <h2 className="font-serif text-4xl font-bold tracking-tight md:text-6xl">
            The Deployment Pipeline
          </h2>

          <p className="mx-auto mt-6 max-w-3xl text-base leading-relaxed text-[#1C1917]/70 md:text-xl">
            Our systematic approach to identifying, funding, and scaling the
            infrastructure that powers artificial intelligence.
          </p>
        </div>

        <div className="relative">
          <div className="absolute left-0 right-0 top-[76px] hidden h-px bg-[#E7D8B8] md:block" />

          <div className="absolute left-0 top-[76px] hidden h-px bg-[#B45309] transition-all duration-300 ease-out md:block" style={{
          width: `${progress}%`,
          boxShadow: "0 0 18px rgba(180, 83, 9, 0.25)"
        }} />

          <div className="grid grid-cols-1 gap-14 md:grid-cols-4 md:gap-8">
            {steps.map((step, index) => {
            const Icon = step.icon;
            const isActive = progress >= index * 25;
            return <div key={step.id} className="relative flex flex-col items-center text-center">
                  <div className={`relative z-10 flex h-[150px] w-[150px] items-center justify-center border transition-all duration-500 ${isActive ? "border-[#D8C29A] bg-[#F5EDD8] shadow-md" : "border-[#E7D8B8] bg-[#FDFAF3]"}`}>
                    <Icon strokeWidth={2} className={`h-12 w-12 transition-colors duration-500 ${isActive ? "text-[#B45309]" : "text-[#1C1917]"}`} />
                  </div>

                  <div className="mt-12">
                    <span className={`font-mono text-sm font-bold tracking-[0.25em] transition-colors duration-500 ${isActive ? "text-[#B45309]" : "text-[#1C1917]"}`}>
                      STAGE {step.id}
                    </span>

                    <h3 className="mt-4 font-serif text-3xl font-bold leading-tight text-[#1C1917]">
                      {step.title}
                    </h3>

                    <p className="mx-auto mt-6 max-w-[280px] text-base leading-relaxed text-[#1C1917]/80">
                      {step.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}